import React from 'react';
import { Image } from 'antd';
import classNames from 'classnames';

import { IOption, ISubQuestion } from 'modules/national-certificate';
import { useCheckNationalTest } from 'modules/national-certificate-test';

import { MathJaxContext } from 'components';

interface IProps {
  subQuestions?: ISubQuestion[];
  options?: IOption[];
  answers?: Record<number, number | null>;
  onUpdateAnswer: (subQuestionId: number, optionId: number | null) => void;
}

const Options: React.FC<IProps> = ({ options, subQuestions, onUpdateAnswer, answers }) => {
  const { mutate } = useCheckNationalTest();

  const findSubQuestionByOption = (optionId: number) =>
    Object.entries(answers ?? {}).find(([_, optId]) => optId === optionId)?.[0];

  const handleOptionClick = (optionId: number) => () => {
    const linkedSubQuestionId = findSubQuestionByOption(optionId);

    if (linkedSubQuestionId) {
      onUpdateAnswer(Number(linkedSubQuestionId), null);
      return;
    }

    const nextUnanswered = subQuestions?.find(q => !answers?.[q.id] || answers[q.id] === 0);

    if (!nextUnanswered) return;

    onUpdateAnswer(nextUnanswered.id, optionId);

    mutate({
      testId: nextUnanswered.id,
      optionId
    });
  };

  return (
    <div className="space-y-1.5">
      {options?.map((option, index) => {
        const isSelected = !!findSubQuestionByOption(option.id);

        return (
          <div
            key={option.id}
            className={classNames('rounded-xl border-2', {
              'border-transparent': !isSelected,
              'border-green-500 bg-green-50': isSelected
            })}
          >
            <div
              className={classNames('flex cursor-pointer items-center gap-3 rounded-xl border p-2.5 transition-all', {
                'border-gray-200': !isSelected,
                'border-transparent': isSelected
              })}
              onClick={handleOptionClick(option.id)}
            >
              <span className="font-semibold text-gray-600">{String.fromCharCode(97 + index)})</span>
              <div className="min-w-0 flex-1 overflow-x-auto overflow-y-hidden">
                <p className="inline-block whitespace-pre-line text-xs text-gray-800">
                  <MathJaxContext expression={option.name} />
                </p>
                {option?.image && (
                  <div className="mt-2 flex justify-center">
                    <Image src={option.image} alt={option?.name || 'Test option'} loading="lazy" title="Test image" />
                  </div>
                )}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default Options;
